import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blocksApi, sectionsApi } from '../services/api';
import { Block, Section, Variable } from '../types';
import FormulaEditor from '../components/FormulaEditor';
import DocumentDesigner, { DesignerDocument, documentToPlainText, uid } from '../components/DocumentDesigner';
import {
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  Blocks,
  Search,
  X,
  Check,
  ChevronDown,
  ChevronUp,
  Upload,
  Info,
  Calculator
} from 'lucide-react';

const createBlockDocument = (content: string): DesignerDocument => ({
  schemaVersion: 1,
  nodes: content ? [{ id: uid(), type: 'paragraph', text: content }] : []
});

/** Klartext-Spiegel eines Bausteins – deckt alle Elementtypen des Editors ab. */
export const blockDocumentToPlainText = (document: DesignerDocument) => documentToPlainText(document);

export default function AdminBlocks() {
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBlock, setEditingBlock] = useState<Block | null>(null);
  const [expandedBlocks, setExpandedBlocks] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [blocksRes, sectionsRes] = await Promise.all([
        blocksApi.getAll(),
        sectionsApi.getAll()
      ]);
      setBlocks(blocksRes.blocks);
      setSections(sectionsRes.sections);
    } catch (err) {
      setError('Fehler beim Laden der Daten');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bauteil wirklich löschen?')) return;
    
    try {
      await blocksApi.delete(id);
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Löschen fehlgeschlagen');
    }
  };

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedBlocks);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedBlocks(newExpanded);
  };

  const filteredBlocks = blocks.filter(block => {
    const matchesSearch = block.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      block.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSection = !selectedSection || 
      (selectedSection === 'none' ? !block.sectionId : block.sectionId === selectedSection);
    return matchesSearch && matchesSection;
  });

  // Blocks ohne Abteil
  const blocksWithoutSection = filteredBlocks.filter(b => !b.sectionId);

  // Group by section
  const groupedBlocks = sections.map(section => ({
    section,
    blocks: filteredBlocks.filter(b => b.sectionId === section.id)
  })).filter(g => g.blocks.length > 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bauteile verwalten</h1>
          <p className="mt-1 text-sm text-gray-500">
            {blocks.length} Bauteil(e) in {sections.length} Abteil(en)
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <Link
            to="/admin/blocks/import"
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 bg-white rounded-lg hover:bg-gray-50"
          >
            <Upload className="h-4 w-4 mr-2" />
            Importieren
          </Link>
          <button
            onClick={() => {
              setEditingBlock(null);
              setShowModal(true);
            }}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Neuer Bauteil
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Bauteil suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            />
          </div>
          <select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
          >
            <option value="">Alle Abteile</option>
            <option value="none">Ohne Abteil (freistehend)</option>
            {sections.map((section) => (
              <option key={section.id} value={section.id}>{section.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {/* Blocks grouped by section */}
      {groupedBlocks.length === 0 && blocksWithoutSection.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Blocks className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Bauteile gefunden</h3>
          <p className="text-gray-500">
            {searchTerm || selectedSection
              ? 'Versuchen Sie andere Suchkriterien'
              : 'Erstellen Sie Ihren ersten Bauteil'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Blocks ohne Abteil */}
          {blocksWithoutSection.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-amber-200 overflow-hidden">
              <div className="bg-amber-50 px-4 py-3 border-b border-amber-200">
                <h3 className="font-medium text-amber-800">Ohne Abteil (freistehend)</h3>
                <p className="text-xs text-amber-600">{blocksWithoutSection.length} Bauteil(e) - ohne §-Nummerierung</p>
              </div>
              <div className="divide-y divide-gray-200">
                {blocksWithoutSection.map((block) => (
                  <div key={block.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <button
                            onClick={() => toggleExpand(block.id)}
                            className="mr-2 p-1 text-gray-400 hover:text-gray-600"
                          >
                            {expandedBlocks.has(block.id) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                          <div>
                            <h4 className="font-medium text-gray-900">{block.name}</h4>
                            <div className="flex items-center mt-1 space-x-4 text-xs text-gray-500">
                              <span>{block.variables.length} Variable(n)</span>
                              <span className={block.isActive ? 'text-green-600' : 'text-gray-400'}>
                                {block.isActive ? 'Aktiv' : 'Inaktiv'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => {
                            setEditingBlock(block);
                            setShowModal(true);
                          }}
                          className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                          title="Bearbeiten"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(block.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                          title="Löschen"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    
                    {expandedBlocks.has(block.id) && (
                      <div className="mt-4 pl-8">
                        <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600 whitespace-pre-wrap mb-3">
                          {block.content}
                        </div>
                        {block.variables.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {block.variables.map((v) => (
                              <span key={v.id} className="px-2 py-1 bg-primary-50 text-primary-700 text-xs rounded">
                                {v.name} ({v.valueType})
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Blocks nach Abteil gruppiert */}
          {groupedBlocks.map(({ section, blocks: sectionBlocks }) => (
            <div key={section.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h3 className="font-medium text-gray-900">{section.name}</h3>
                <p className="text-xs text-gray-500">{sectionBlocks.length} Bauteil(e)</p>
              </div>
              <div className="divide-y divide-gray-200">
                {sectionBlocks.map((block) => (
                  <div key={block.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <button
                            onClick={() => toggleExpand(block.id)}
                            className="mr-2 p-1 text-gray-400 hover:text-gray-600"
                          >
                            {expandedBlocks.has(block.id) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                          <div>
                            <h4 className="font-medium text-gray-900">{block.name}</h4>
                            <div className="flex items-center mt-1 space-x-4 text-xs text-gray-500">
                              <span>{block.variables.length} Variable(n)</span>
                              <span className={block.isActive ? 'text-green-600' : 'text-gray-400'}>
                                {block.isActive ? 'Aktiv' : 'Inaktiv'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => {
                            setEditingBlock(block);
                            setShowModal(true);
                          }}
                          className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                          title="Bearbeiten"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(block.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                          title="Löschen"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    
                    {expandedBlocks.has(block.id) && (
                      <div className="mt-4 pl-8">
                        <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600 whitespace-pre-wrap mb-3">
                          {block.content}
                        </div>
                        {block.variables.length > 0 && (
                          <div>
                            <h5 className="text-xs font-medium text-gray-500 mb-2">Variablen:</h5>
                            <div className="flex flex-wrap gap-2">
                              {block.variables.map((v) => (
                                <span
                                  key={v.id}
                                  className="px-2 py-1 bg-primary-50 text-primary-700 text-xs rounded"
                                >
                                  {v.label} ({v.valueType})
                                  {v.required && <span className="text-red-500 ml-1">*</span>}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Block Modal */}
      {showModal && (
        <BlockModal
          block={editingBlock}
          sections={sections}
          allBlocks={blocks}
          onClose={() => {
            setShowModal(false);
            setEditingBlock(null);
          }}
          onSave={async () => {
            await loadData();
            setShowModal(false);
            setEditingBlock(null);
          }}
        />
      )}
    </div>
  );
}

// Block Modal Component
function BlockModal({
  block,
  sections,
  allBlocks,
  onClose,
  onSave
}: {
  block: Block | null;
  sections: Section[];
  allBlocks: Block[];
  onClose: () => void;
  onSave: () => void;
}) {
  const [formData, setFormData] = useState({
    name: block?.name || '',
    content: block?.content || '',
    sectionId: block?.sectionId || '',
    isActive: block?.isActive ?? true
  });
  const [editorMode, setEditorMode] = useState<'plain' | 'structured'>(block?.documentJson ? 'structured' : 'plain');
  const [blockDocument, setBlockDocument] = useState<DesignerDocument>(() => {
    if (block?.documentJson) {
      try { return JSON.parse(block.documentJson); } catch { /* Klartext-Fallback verwenden */ }
    }
    return createBlockDocument(block?.content || '');
  });
  const [variables, setVariables] = useState<Partial<Variable>[]>(
    block?.variables || []
  );
  const [detectedVars, setDetectedVars] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showVariablePicker, setShowVariablePicker] = useState(false);
  const [variableSearch, setVariableSearch] = useState('');
  const [showFormulaEditor, setShowFormulaEditor] = useState(false);
  const [editingFormulaIndex, setEditingFormulaIndex] = useState<number | null>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Alle existierenden Variablen aus anderen Blöcken sammeln
  const existingVariables = React.useMemo(() => {
    const vars: { name: string; label: string; blockName: string; valueType: string }[] = [];
    allBlocks.forEach(b => {
      if (b.id !== block?.id && b.variables) {
        b.variables.forEach(v => {
          if (!vars.find(ev => ev.name === v.name)) {
            vars.push({
              name: v.name,
              label: v.label,
              blockName: b.name,
              valueType: v.valueType
            });
          }
        });
      }
    });
    return vars;
  }, [allBlocks, block?.id]);

  const filteredExistingVariables = variableSearch
    ? existingVariables.filter(v =>
        v.name.toLowerCase().includes(variableSearch.toLowerCase()) ||
        v.label.toLowerCase().includes(variableSearch.toLowerCase())
      )
    : existingVariables;

  // Detect variables when content changes
  useEffect(() => {
    const regex = /<<([^>>]+)>>/g;
    const found: string[] = [];
    let match;
    while ((match = regex.exec(formData.content)) !== null) {
      if (!found.includes(match[1])) {
        found.push(match[1]);
      }
    }
    setDetectedVars(found);
  }, [formData.content]);

  const handleAddVariable = (name: string, fromExisting?: { label: string; valueType: string }) => {
    if (!variables.find(v => v.name === name)) {
      setVariables([...variables, {
        name,
        label: fromExisting?.label || name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        valueType: (fromExisting?.valueType || 'TEXT') as 'TEXT' | 'NUMBER' | 'DATE' | 'CURRENCY' | 'BOOLEAN' | 'SELECT',
        required: true
      }]);
    }
  };

  const handleInsertVariable = (varName: string, existingVar?: { label: string; valueType: string }) => {
    if (editorMode === 'structured') {
      const token = `<<${varName}>>`;
      const nodes = [...blockDocument.nodes];
      const lastTextIndex = nodes.map(node => node.type === 'paragraph' || node.type === 'heading').lastIndexOf(true);
      if (lastTextIndex >= 0) {
        const node = nodes[lastTextIndex];
        nodes[lastTextIndex] = { ...node, text: `${node.text || ''}${node.text ? ' ' : ''}${token}` };
      } else {
        nodes.push({ id: uid(), type: 'paragraph', text: token });
      }
      const next = { ...blockDocument, nodes };
      setBlockDocument(next);
      setFormData(current => ({ ...current, content: blockDocumentToPlainText(next) }));
      handleAddVariable(varName, existingVar);
      setShowVariablePicker(false);
      setVariableSearch('');
      return;
    }
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.content;
      const before = text.substring(0, start);
      const after = text.substring(end);
      const newContent = `${before}<<${varName}>>${after}`;
      setFormData({ ...formData, content: newContent });
      
      // Variable auch zur Liste hinzufügen
      handleAddVariable(varName, existingVar);
    }
    setShowVariablePicker(false);
    setVariableSearch('');
  };

  const handleRemoveVariable = (name: string) => {
    setVariables(variables.filter(v => v.name !== name));
  };

  const handleVariableChange = (index: number, field: string, value: any) => {
    const updated = [...variables];
    updated[index] = { ...updated[index], [field]: value };
    setVariables(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.content) {
      setError('Bitte Name und Inhalt ausfüllen');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const payload = editorMode === 'structured'
        ? { ...formData, content: blockDocumentToPlainText(blockDocument) }
        : formData;
      if (block) {
        await blocksApi.update(block.id, {
          ...payload,
          documentJson: editorMode === 'structured' ? JSON.stringify(blockDocument) : null,
          variables: variables as any
        });
      } else {
        await blocksApi.create({
          ...payload,
          documentJson: editorMode === 'structured' ? JSON.stringify(blockDocument) : null,
          variables: variables as any
        });
      }
      onSave();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.errors?.map((item: { msg: string }) => item.msg).join('; ') || 'Speichern fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className={`bg-white rounded-xl shadow-xl w-full max-h-[90vh] overflow-y-auto ${editorMode === 'structured' ? 'max-w-7xl' : 'max-w-3xl'}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              {block ? 'Bauteil bearbeiten' : 'Neuer Bauteil'}
            </h2>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="z.B. Vertragsparteien"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Abteil
                </label>
                <select
                  value={formData.sectionId}
                  onChange={(e) => setFormData({ ...formData, sectionId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">-- Ohne Abteil (freistehend) --</option>
                  {sections.map((section) => (
                    <option key={section.id} value={section.id}>{section.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Bauteile ohne Abteil erhalten keine §-Nummerierung (z.B. für Überschriften)
                </p>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Inhalt <span className="text-red-500">*</span>
                </label>
                <div className="relative flex items-center gap-2">
                  <div className="inline-flex rounded-lg border border-gray-200 p-0.5" aria-label="Editormodus">
                    <button type="button" onClick={() => setEditorMode('plain')} className={`px-3 py-1 text-xs rounded-md ${editorMode === 'plain' ? 'bg-gray-100 font-medium' : ''}`}>Klartext</button>
                    <button type="button" onClick={() => {
                      if (editorMode === 'plain') setBlockDocument(createBlockDocument(formData.content));
                      setEditorMode('structured');
                    }} className={`px-3 py-1 text-xs rounded-md ${editorMode === 'structured' ? 'bg-primary-100 text-primary-700 font-medium' : ''}`}>Rich-Text &amp; Layout</button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowVariablePicker(!showVariablePicker)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Variable einfügen
                  </button>
                  
                  {/* Variable Picker Dropdown */}
                  {showVariablePicker && (
                    <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                      <div className="p-2 border-b border-gray-200">
                        <input
                          type="text"
                          value={variableSearch}
                          onChange={(e) => setVariableSearch(e.target.value)}
                          placeholder="Variable suchen oder neu erstellen..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                          autoFocus
                        />
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {/* Neue Variable erstellen */}
                        {variableSearch && !existingVariables.find(v => v.name === variableSearch) && (
                          <button
                            type="button"
                            onClick={() => handleInsertVariable(variableSearch.toLowerCase().replace(/\s+/g, '_'))}
                            className="w-full px-3 py-2 text-left hover:bg-green-50 flex items-center gap-2 border-b border-gray-100"
                          >
                            <Plus className="h-4 w-4 text-green-500" />
                            <div>
                              <div className="text-sm font-medium text-green-700">
                                Neue Variable: &lt;&lt;{variableSearch.toLowerCase().replace(/\s+/g, '_')}&gt;&gt;
                              </div>
                              <div className="text-xs text-gray-500">Neue Variable erstellen</div>
                            </div>
                          </button>
                        )}
                        
                        {/* Existierende Variablen */}
                        {filteredExistingVariables.length > 0 ? (
                          <>
                            <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase">
                              Existierende Variablen
                            </div>
                            {filteredExistingVariables.map((v) => (
                              <button
                                key={v.name}
                                type="button"
                                onClick={() => handleInsertVariable(v.name, { label: v.label, valueType: v.valueType })}
                                className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-start gap-2"
                              >
                                <Check className="h-4 w-4 text-primary-500 mt-0.5" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{v.label}</div>
                                  <div className="text-xs text-gray-500">
                                    {v.blockName} · &lt;&lt;{v.name}&gt;&gt; · {v.valueType}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </>
                        ) : (
                          !variableSearch && (
                            <div className="px-3 py-4 text-sm text-gray-500 text-center">
                              Keine existierenden Variablen gefunden.
                              <br />
                              Geben Sie einen Namen ein, um eine neue zu erstellen.
                            </div>
                          )
                        )}
                      </div>
                      <div className="p-2 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => {
                            setShowVariablePicker(false);
                            setVariableSearch('');
                          }}
                          className="w-full px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 rounded"
                        >
                          Schließen
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-2">Variablen mit &lt;&lt;variablen_name&gt;&gt; einfügen. Der Klartext wird für alle bestehenden Ausgaben weiterhin automatisch gepflegt.</p>
              {editorMode === 'plain' ? (
                <textarea ref={textareaRef} value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} rows={10} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm" placeholder="Der Mieter <<mieter_name>> wohnhaft in <<mieter_adresse>>..." />
              ) : (
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <DocumentDesigner
                    value={blockDocument}
                    variables={variables.map(variable => variable.name || '').filter(Boolean)}
                    onChange={(next) => {
                      setBlockDocument(next);
                      setFormData(current => ({ ...current, content: blockDocumentToPlainText(next) }));
                    }}
                  />
                </div>
              )}
            </div>

            {/* Detected Variables */}
            {detectedVars.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Erkannte Variablen im Text:
                </label>
                <div className="flex flex-wrap gap-2">
                  {detectedVars.map((name) => {
                    const exists = variables.find(v => v.name === name);
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => !exists && handleAddVariable(name)}
                        className={`px-3 py-1 text-sm rounded-full ${
                          exists
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        }`}
                      >
                        {name}
                        {exists ? <Check className="h-3 w-3 inline ml-1" /> : <Plus className="h-3 w-3 inline ml-1" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Variables List */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Variablen konfigurieren:
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setEditingFormulaIndex(null);
                    setShowFormulaEditor(true);
                  }}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100"
                >
                  <Calculator className="h-3 w-3 mr-1" />
                  Formel-Variable erstellen
                </button>
              </div>
              {variables.length > 0 && (
                <div className="space-y-4">
                  {variables.map((variable, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      {/* Header Row */}
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium text-gray-700">&lt;&lt;{variable.name}&gt;&gt;</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveVariable(variable.name || '')}
                          className="p-1 text-red-500 hover:text-red-700"
                          title="Variable entfernen"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {/* Main Fields Row */}
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Bezeichnung</label>
                          <input
                            type="text"
                            value={variable.label || ''}
                            onChange={(e) => handleVariableChange(index, 'label', e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                            placeholder="Anzeigename im Formular"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Typ</label>
                          <select
                            value={variable.valueType || 'TEXT'}
                            onChange={(e) => handleVariableChange(index, 'valueType', e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                          >
                            <option value="TEXT">Text</option>
                            <option value="NUMBER">Zahl</option>
                            <option value="DATE">Datum</option>
                            <option value="CURRENCY">Währung (€)</option>
                            <option value="BOOLEAN">Ja/Nein</option>
                            <option value="SELECT">Auswahl (Dropdown)</option>
                            <option value="FORMULA">Formel (berechnet)</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Standardwert</label>
                          <input
                            type="text"
                            value={variable.defaultValue || ''}
                            onChange={(e) => handleVariableChange(index, 'defaultValue', e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                            placeholder="Optional"
                          />
                        </div>
                      </div>

                      {/* Options Row (nur für SELECT) */}
                      {variable.valueType === 'SELECT' && (
                        <div className="mb-3">
                          <label className="block text-xs text-gray-500 mb-1">
                            Auswahloptionen <span className="text-gray-400">(kommagetrennt)</span>
                          </label>
                          <input
                            type="text"
                            value={variable.options || ''}
                            onChange={(e) => handleVariableChange(index, 'options', e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                            placeholder="Option 1, Option 2, Option 3"
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            Trennen Sie die Optionen mit Kommas
                          </p>
                        </div>
                      )}

                      {/* Formula Row (nur für FORMULA) */}
                      {variable.valueType === 'FORMULA' && (
                        <div className="mb-3">
                          <label className="block text-xs text-gray-500 mb-1 flex items-center">
                            <Calculator className="h-3 w-3 mr-1" />
                            Formel
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={variable.options || ''}
                              onChange={(e) => handleVariableChange(index, 'options', e.target.value)}
                              className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm font-mono bg-gray-50"
                              placeholder="z.B. <<hauptmietzins>> + <<betriebskosten>>"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setEditingFormulaIndex(index);
                                setShowFormulaEditor(true);
                              }}
                              className="px-3 py-1.5 bg-primary-100 text-primary-700 rounded text-sm hover:bg-primary-200 flex items-center"
                            >
                              <Calculator className="h-4 w-4 mr-1" />
                              Editor
                            </button>
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            Verwenden Sie {"<<variablenname>>"} und Operatoren (+, -, *, /)
                          </p>
                        </div>
                      )}

                      {/* Tooltip/Help Text Row */}
                      <div className="mb-3">
                        <label className="block text-xs text-gray-500 mb-1 flex items-center">
                          <Info className="h-3 w-3 mr-1" />
                          Hilfetext (Tooltip)
                        </label>
                        <input
                          type="text"
                          value={variable.helpText || ''}
                          onChange={(e) => handleVariableChange(index, 'helpText', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                          placeholder="Beschreibung oder Hinweis für dieses Feld"
                        />
                      </div>

                      {/* Checkbox Row */}
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center text-sm text-gray-600">
                          <input
                            type="checkbox"
                            checked={variable.required ?? true}
                            onChange={(e) => handleVariableChange(index, 'required', e.target.checked)}
                            className="mr-1.5 rounded border-gray-300"
                          />
                          Pflichtfeld
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="isActive" className="text-sm text-gray-700">
                Bauteil aktiv (für neue Verträge verfügbar)
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? 'Wird gespeichert...' : 'Speichern'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Formula Editor Modal */}
      <FormulaEditor
        isOpen={showFormulaEditor}
        onClose={() => {
          setShowFormulaEditor(false);
          setEditingFormulaIndex(null);
        }}
        onSave={(formulaData) => {
          if (editingFormulaIndex !== null) {
            // Bearbeiten einer bestehenden Formel-Variable
            const updated = [...variables];
            updated[editingFormulaIndex] = {
              ...updated[editingFormulaIndex],
              name: formulaData.name,
              label: formulaData.label,
              options: formulaData.formula,
              helpText: formulaData.helpText,
              valueType: 'FORMULA'
            };
            setVariables(updated);
          } else {
            // Neue Formel-Variable erstellen
            // Füge auch die Variable in den Content ein
            const textarea = textareaRef.current;
            if (textarea) {
              const start = textarea.selectionStart;
              const text = formData.content;
              const before = text.substring(0, start);
              const after = text.substring(start);
              setFormData({ 
                ...formData, 
                content: `${before}<<${formulaData.name}>>${after}` 
              });
            }
            
            setVariables([...variables, {
              name: formulaData.name,
              label: formulaData.label,
              valueType: 'FORMULA' as const,
              options: formulaData.formula,
              helpText: formulaData.helpText,
              required: false
            }]);
          }
          setShowFormulaEditor(false);
          setEditingFormulaIndex(null);
        }}
        availableVariables={[
          ...variables.filter(v => ['NUMBER', 'CURRENCY', 'FORMULA'].includes(v.valueType || '')).map(v => ({
            name: v.name || '',
            label: v.label || '',
            valueType: v.valueType || 'NUMBER'
          })),
          ...existingVariables.filter(v => ['NUMBER', 'CURRENCY', 'FORMULA'].includes(v.valueType))
        ]}
        initialData={editingFormulaIndex !== null ? {
          name: variables[editingFormulaIndex]?.name || '',
          label: variables[editingFormulaIndex]?.label || '',
          formula: variables[editingFormulaIndex]?.options || '',
          helpText: variables[editingFormulaIndex]?.helpText
        } : undefined}
      />
    </div>
  );
}
